import { ChangeDetectionStrategy, Component, computed, effect, input, model, signal } from "@angular/core";
import { MonacoEditorModule } from "ngx-monaco-editor-v2";
import type { editor as MonacoEditor } from "monaco-editor";
import { applyEditorMarkers, EditorMarker } from "./editor-lint";

/**
 * Plain single-pane Monaco editor.
 * Use when there is no original content to diff against (e.g. creating a new file).
 */
@Component({
  selector: "app-monaco-editor",
  templateUrl: "./monaco-editor.component.html",
  styleUrl: "./monaco-editor.component.less",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MonacoEditorModule],
})
export class MonacoEditorComponent {
  readonly content = model<string>("");
  readonly language = input<string>("plaintext");
  readonly height = input<string>("500px");
  /** Lint markers rendered as squiggles on the content. */
  readonly markers = input<EditorMarker[]>([]);

  protected readonly editorOptions = computed(() => ({
    theme: "vs-dark",
    language: this.language(),
    automaticLayout: true,
    scrollbar: { verticalScrollbarSize: 10, horizontalScrollbarSize: 10 },
  }));

  private editorInstance: MonacoEditor.IStandaloneCodeEditor | null = null;
  private readonly editorReady = signal(false);

  constructor() {
    effect(() => {
      const value = this.content();
      if (this.editorInstance && this.editorInstance.getValue() !== value) {
        this.editorInstance.setValue(value);
      }
    });

    effect(() => {
      const markers = this.markers();
      if (this.editorReady()) {
        applyEditorMarkers(this.editorInstance.getModel(), markers);
      }
    });
  }

  protected onEditorInit(editor: MonacoEditor.IStandaloneCodeEditor): void {
    this.editorInstance = editor;
    editor.setValue(this.content());
    editor.onDidChangeModelContent(() => {
      this.content.set(editor.getValue());
    });
    this.editorReady.set(true);
  }
}
